// @flow
import React, { Component, type Element } from 'react';
import { isEqual, noop, pull } from 'lodash';
import { forceCheck } from 'react-lazyload';

type State = {
  activeIndexes: Array<number>,
  lastIndex: number,
  isPristine: boolean,
};

type Props = {
  activeIndexes?: Array<number>,
  children: ({
    activeIndexes: Array<number>,
    clear: () => void,
    isPristine: boolean,
    lastIndex: ?number,
    update: (index: number) => void,
  }) => Element<*>,
  hasMultipleIndexEnabled?: boolean,
  hasNoIndexEnabled?: boolean,
  onUpdate?: (index: number) => void,
};

export class ActiveIndexes extends Component<Props, State> {
  static defaultProps = {
    activeIndexes: [],
    hasMultipleIndexEnabled: false,
    hasNoIndexEnabled: false,
  };

  state = {
    activeIndexes: this.props.activeIndexes || [],
    isPristine: true,
    lastIndex: 0,
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { activeIndexes = [] } = this.props;
    if (!isEqual(prevProps.activeIndexes, activeIndexes)) {
      const lastIndex = activeIndexes[activeIndexes.length - 1];
      this.setState({ activeIndexes, lastIndex: lastIndex >= 0 ? lastIndex : prevState.lastIndex });
    }
  }

  addIndex(activeIndexes: Array<number>, index: number): Array<number> {
    const { hasMultipleIndexEnabled } = this.props;

    if (hasMultipleIndexEnabled) {
      return [...activeIndexes, index];
    }
    return [index];
  }

  removeIndex(activeIndexes: Array<number>, index: number): Array<number> {
    const { hasNoIndexEnabled } = this.props;

    if (hasNoIndexEnabled || activeIndexes.length > 1) {
      return pull(activeIndexes, index);
    }
    return activeIndexes;
  }

  clear = () => {
    this.setState(prevState => {
      if (prevState.activeIndexes.length) return { activeIndexes: [] };
    });
  };

  update = (index: number) => {
    const { onUpdate = noop } = this.props;
    const { activeIndexes } = this.state;

    const newActiveIndexes = activeIndexes.includes(index)
      ? this.removeIndex(activeIndexes, index)
      : this.addIndex(activeIndexes, index);

    this.setState(
      {
        activeIndexes: newActiveIndexes,
        isPristine: false,
        lastIndex: index,
      },
      () => {
        onUpdate(index);
        forceCheck();
      },
    );
  };

  render() {
    const { children } = this.props;
    return children({
      activeIndexes: this.state.activeIndexes,
      clear: this.clear,
      isPristine: this.state.isPristine,
      lastIndex: this.state.lastIndex,
      update: this.update,
    });
  }
}
